<template>
  <!-- The invoice editor (lib/view/invoices/manage_invoice_details/): the Details tab with the
       title block (client and status tag), Items with the Catalog button, the totals
       and the Payment / Online page extras; the Actions menu; and the Payment tab with the
       installment schedule. Labels are the app's strings (editInvoice, details, auditHistory,
       repeatTab, invoiceTitleStatus*, items/catalog, subtotal, total, invoicePaymentInFull /
       invoicePaymentInstallments, onlinePage, saveAndExit, saveAndCopyLink, saveAndSendEmail,
       saveAndSendSms, amountDue, invoicePaidOfTotal, installment Paid {date} / Due next /
       Upcoming, recordPayment, paymentLink, footer "{n} items" · Total · Due {date}). The client,
       items and amounts are sample data and match the hero list (INV-1042, $2,450.00).
       States: items → payment → send → track; the server renders `track`. -->
  <div class="rp iew" :class="`iew--${state}`" aria-hidden="true">
    <header class="rp-head">
      <b>Edit invoice</b>
      <span class="iew-head-actions">
        <span class="rp-btn-quiet" :class="{ 'is-hot': state === 'send' }">Actions</span>
        <span class="rp-btn">Save</span>
      </span>
    </header>
    <nav class="iew-tabs">
      <span :class="{ 'is-on': !onPaymentTab }">Details</span>
      <span :class="{ 'is-on': onPaymentTab }">Payment</span>
      <span>Audit history</span>
      <span>Repeat</span>
    </nav>

    <div class="iew-screens">
      <!-- Details -->
      <section class="iew-screen iew-s-details">
        <div class="iew-title">
          <span>
            <b>Brand refresh, phase 2</b>
            <small>Northwind Studio · number set when you save</small>
          </span>
          <em class="rp-chip">Draft</em>
        </div>
        <div class="iew-items">
          <div class="iew-items-head">
            <b>Items</b>
            <span class="rp-btn-quiet" :class="{ 'is-hot': state === 'items' }"><svg viewBox="0 0 24 24"><path :d="book" /></svg>Catalog</span>
          </div>
          <div v-for="(item, i) in items" :key="item.name" class="iew-item" :class="{ 'is-new': i === 2 }">
            <span><b>{{ item.name }}</b><small>{{ item.qty }} × {{ item.rate }}</small></span>
            <span class="rp-num">{{ item.amount }}</span>
          </div>
          <div class="iew-totals">
            <b>Total</b><b class="rp-num">$2,450.00</b>
          </div>
        </div>
        <dl class="iew-extras">
          <div :class="{ 'is-hot': state === 'payment' }">
            <dt>Payment</dt>
            <dd class="iew-swap">
              <span class="iew-swap-a">In full</span>
              <span class="iew-swap-b">3 installments</span>
            </dd>
          </div>
          <div><dt>Online page</dt><dd>On</dd></div>
        </dl>

        <!-- The catalog popover while an item is added. -->
        <div class="iew-pop iew-pop--catalog">
          <small>Catalog</small>
          <span>Brand strategy workshop</span>
          <span>Logo system</span>
          <span class="is-hot">Social templates</span>
          <span>Website copy</span>
        </div>
        <!-- The Actions menu while it is sent. -->
        <div class="iew-pop iew-pop--actions">
          <span>Save &amp; Exit</span>
          <span>Save &amp; Copy Link</span>
          <span class="is-hot"><svg viewBox="0 0 24 24"><path :d="mail" /></svg>Save &amp; Send Email</span>
          <span>Save &amp; Send SMS</span>
        </div>
      </section>

      <!-- Payment tab, once the client has paid the first installment. -->
      <section class="iew-screen iew-s-track">
        <div class="iew-title">
          <span>
            <b>Brand refresh, phase 2</b>
            <small>INV-1042 · Northwind Studio</small>
          </span>
          <em class="rp-chip rp-chip--amber">Partly paid</em>
        </div>
        <div class="iew-due">
          <small>Amount due</small>
          <b class="rp-num">$1,450.00</b>
          <span>Paid $1,000.00 of $2,450.00 · next payment due Dec 4, 2026</span>
          <i class="iew-progress"><i /></i>
        </div>
        <ol class="iew-plan">
          <li><span class="rp-num">$1,000.00</span><span>Nov 4, 2026</span><em class="rp-chip rp-chip--green">Paid Nov 3</em></li>
          <li><span class="rp-num">$725.00</span><span>Dec 4, 2026</span><em class="rp-chip rp-chip--blue">Due next</em></li>
          <li><span class="rp-num">$725.00</span><span>Jan 4, 2027</span><em class="rp-chip">Upcoming</em></li>
        </ol>
        <div class="iew-pay-actions">
          <span class="rp-btn">Record payment</span>
          <span class="rp-btn-quiet">Payment link</span>
        </div>
      </section>
    </div>

    <footer class="iew-foot">
      <span>3 items</span>
      <span>Total <b class="rp-num">$2,450.00</b></span>
      <span>Due Nov 4, 2026</span>
    </footer>
    <div class="rp-toast" :class="{ 'is-off': state !== 'send' }">
      <svg viewBox="0 0 24 24"><path :d="mail" /></svg><span>Save &amp; Send Email</span>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  // items | payment | send | track
  state: { type: String, default: "track" },
});
const onPaymentTab = computed(() => props.state === "track");
const items = [
  { name: "Brand strategy workshop", qty: 1, rate: "$900.00", amount: "$900.00" },
  { name: "Logo system", qty: 1, rate: "$1,200.00", amount: "$1,200.00" },
  { name: "Social templates", qty: 7, rate: "$50.00", amount: "$350.00" },
];
const book = "M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z";
const mail = "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z";
</script>

<style>
.iew { position: relative; }
.iew-head-actions { display: flex; gap: 6px; }
.iew-tabs { display: flex; gap: 14px; border-bottom: 1px solid var(--rp-line); background: var(--rp-page); padding: 0 14px; color: var(--rp-muted); font-size: 12px; white-space: nowrap; overflow: hidden; }
.iew-tabs span { padding: 8px 0 7px; border-bottom: 2px solid transparent; transition: color 0.3s ease, border-color 0.3s ease; }
.iew-tabs .is-on { border-bottom-color: var(--rp-ink); color: var(--rp-text); font-weight: 600; }
.iew-screens { position: relative; flex: 1; min-height: 0; }
.iew-screen { position: absolute; inset: 0; display: grid; align-content: start; gap: 10px; padding: 12px 14px; transition: opacity 0.35s ease, transform 0.45s cubic-bezier(0.33, 1, 0.68, 1); }
.iew-s-details { opacity: 0; transform: translateY(6px); pointer-events: none; }
.iew--items .iew-s-details, .iew--payment .iew-s-details, .iew--send .iew-s-details { opacity: 1; transform: none; }
.iew-s-track { opacity: 0; transform: translateY(6px); }
.iew--track .iew-s-track { opacity: 1; transform: none; }

.iew-title { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.iew-title > span { display: grid; min-width: 0; }
.iew-title b { font-size: 15px; font-weight: 600; }
.iew-title small { color: var(--rp-muted); font-size: 11.5px; }
.iew-props, .iew-extras { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin: 0; }
.iew-props > div, .iew-extras > div { display: grid; gap: 1px; border: 1px solid var(--rp-line); border-radius: 7px; background: var(--rp-card); padding: 6px 9px; transition: box-shadow 0.3s ease; }
.iew-props dt, .iew-extras dt { color: var(--rp-muted); font-size: 11px; }
.iew-props dd, .iew-extras dd { margin: 0; font-weight: 600; }
.iew-items { border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 8px 10px; }
.iew-items-head { display: flex; align-items: center; justify-content: space-between; padding-bottom: 6px; }
.iew-items-head b { font-weight: 600; }
.iew-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; border-top: 1px solid var(--rp-line); padding: 5px 0; }
.iew-item > span:first-child { display: grid; min-width: 0; }
.iew-item b { overflow: hidden; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.iew-item small { color: var(--rp-muted); font-size: 11px; }
.iew-totals { display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; border-top: 1px solid var(--rp-line); padding-top: 6px; color: var(--rp-muted); }
.iew-totals b { color: var(--rp-text); font-weight: 600; }
.iew-swap { position: relative; display: grid; }
.iew-swap > span { grid-area: 1 / 1; transition: opacity 0.3s ease; }
.iew-swap-b { opacity: 0; }
.iew--payment .iew-swap-a, .iew--send .iew-swap-a { opacity: 0; }
.iew--payment .iew-swap-b, .iew--send .iew-swap-b { opacity: 1; }
.is-hot { box-shadow: 0 0 0 2px #1ba7ce; }

.iew-pop {
  position: absolute;
  z-index: 2;
  display: grid;
  gap: 2px;
  min-width: 190px;
  border: 1px solid var(--rp-line);
  border-radius: 8px;
  background: var(--rp-page);
  padding: 6px;
  box-shadow: 0 16px 34px -14px rgb(0 0 0 / 0.4);
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.33, 1, 0.68, 1);
  pointer-events: none;
}
.iew-pop > span, .iew-pop > small { display: flex; align-items: center; gap: 6px; border-radius: 5px; padding: 5px 8px; white-space: nowrap; }
.iew-pop > small { color: var(--rp-muted); font-size: 11px; padding-bottom: 2px; }
.iew-pop .is-hot { box-shadow: none; background: var(--rp-hover); font-weight: 600; }
.iew-pop--catalog { top: 96px; right: 14px; }
.iew-pop--actions { top: 4px; right: 14px; }
.iew--items .iew-pop--catalog, .iew--send .iew-pop--actions { opacity: 1; transform: none; }
.iew--items .iew-item.is-new { animation: iew-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) 0.6s both; }
@keyframes iew-in { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: none; } }

.iew-due { display: grid; gap: 3px; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 10px 12px; }
.iew-due small { color: var(--rp-muted); font-size: 11.5px; }
.iew-due b { font-size: 20px; font-weight: 600; }
.iew-due span { color: var(--rp-muted); font-size: 12px; }
.iew-progress { display: block; height: 6px; margin-top: 4px; overflow: hidden; border-radius: 3px; background: var(--rp-grey-fill); }
.iew-progress i { display: block; width: 41%; height: 100%; border-radius: 3px; background: var(--rp-green); transform-origin: left; }
.iew--track .iew-progress i { animation: iew-fill 0.9s cubic-bezier(0.33, 1, 0.68, 1) 0.3s both; }
@keyframes iew-fill { from { transform: scaleX(0); } to { transform: none; } }
.iew-plan { display: grid; margin: 0; padding: 0; list-style: none; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); }
.iew-plan li { display: grid; grid-template-columns: 1fr 1fr auto; align-items: center; gap: 8px; padding: 7px 10px; }
.iew-plan li + li { border-top: 1px solid var(--rp-line); }
.iew-plan li > span:nth-child(2) { color: var(--rp-muted); font-size: 12px; }
.iew-pay-actions { display: flex; gap: 6px; }
.iew-foot { display: flex; justify-content: space-between; gap: 8px; border-top: 1px solid var(--rp-line); background: var(--rp-page); padding: 8px 14px; color: var(--rp-muted); font-size: 12px; white-space: nowrap; }
.iew-foot b { color: var(--rp-text); font-weight: 600; }
.iew .rp-toast { bottom: 48px; }
/* Phones: drop the secondary lines so each screen fits the stage. */
@container (max-width: 340px) {
  .iew-foot span:last-child, .iew-item small, .iew-plan li > span:nth-child(2), .iew-pay-actions, .iew-title small { display: none; }
  .iew-screen { gap: 8px; padding: 10px 12px; }
  .iew-title b { font-size: 14px; }
  .iew-plan li { grid-template-columns: 1fr auto; }
  .iew-tabs { gap: 12px; }
  .iew-pop--catalog { top: 70px; }
}
.iew { container-type: inline-size; }
</style>
